/**
 * TBMuktBharat 2026 Data Display Components
 * Advanced components for data visualization and display
 */

import React, { forwardRef, useState, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

// ================================================================================
// ANIMATED COUNTER COMPONENT
// ================================================================================
interface AnimatedCounterProps {
  value: number
  duration?: number
  decimals?: number
  prefix?: string
  suffix?: string
  className?: string
}

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  duration = 1000,
  decimals = 0,
  prefix = '',
  suffix = '',
  className,
}) => {
  const [displayValue, setDisplayValue] = useState(0)
  const startTime = useRef<number | null>(null)
  const startValue = useRef(0)

  useEffect(() => {
    startValue.current = displayValue
    startTime.current = null

    const animate = (currentTime: number) => {
      if (!startTime.current) startTime.current = currentTime
      const elapsed = currentTime - startTime.current
      const progress = Math.min(elapsed / duration, 1)

      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const currentValue = startValue.current + (value - startValue.current) * easeOutQuart

      setDisplayValue(currentValue)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [value, duration])

  return (
    <span className={cn('tabular-nums', className)}>
      {prefix}{displayValue.toFixed(decimals)}{suffix}
    </span>
  )
}

// ================================================================================
// STATS CARD WITH ANIMATION COMPONENT
// ================================================================================
interface StatsCardProps {
  icon?: React.ReactNode
  label: string
  value: number | string
  change?: number
  changeLabel?: string
  trend?: 'up' | 'down' | 'neutral'
  animated?: boolean
  iconBgColor?: string
  className?: string
  onClick?: () => void
}

export const StatsCard: React.FC<StatsCardProps> = ({
  icon,
  label,
  value,
  change,
  changeLabel = 'from last month',
  trend,
  animated = true,
  iconBgColor = 'bg-primary-100 dark:bg-primary-900/30',
  className,
  onClick,
}) => {
  const trendDirection = trend || (change !== undefined ? (change >= 0 ? 'up' : 'down') : 'neutral')

  return (
    <motion.div
      whileHover={onClick ? { scale: 1.02 } : {}}
      whileTap={onClick ? { scale: 0.98 } : {}}
      className={cn(
        'bg-white dark:bg-neutral-900 rounded-xl p-6 border border-neutral-200 dark:border-neutral-800',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="text-3xl font-bold text-neutral-900 dark:text-white mt-2">
            {animated && typeof value === 'number' ? (
              <AnimatedCounter value={value} />
            ) : (
              value
            )}
          </p>
          {change !== undefined && (
            <div className="flex items-center gap-2 mt-3">
              <span className={cn(
                'inline-flex items-center text-sm font-medium rounded-full px-2 py-0.5',
                trendDirection === 'up' && 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400',
                trendDirection === 'down' && 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-400',
                trendDirection === 'neutral' && 'text-neutral-700 bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-400'
              )}>
                {trendDirection === 'up' && '↑'}
                {trendDirection === 'down' && '↓'}
                {trendDirection === 'neutral' && '→'}
                <span className="ml-1">{Math.abs(change)}%</span>
              </span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400">{changeLabel}</span>
            </div>
          )}
        </div>
        {icon && (
          <div className={cn('p-3 rounded-xl', iconBgColor)}>
            {icon}
          </div>
        )}
      </div>
    </motion.div>
  )
}

// ================================================================================
// DATA TABLE COMPONENT
// ================================================================================
interface Column<T> {
  key: keyof T | string
  header: string
  width?: string
  sortable?: boolean
  render?: (value: any, row: T) => React.ReactNode
}

interface DataTableProps<T> {
  data: T[]
  columns: Column<T>[]
  keyExtractor: (item: T) => string | number
  onRowClick?: (item: T) => void
  sortable?: boolean
  emptyMessage?: string
  className?: string
  loading?: boolean
  pageSize?: number
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  keyExtractor,
  onRowClick,
  sortable = true,
  emptyMessage = 'No data available',
  className,
  loading = false,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const sortedData = useMemo(() => {
    if (!sortKey || !sortable) return data

    return [...data].sort((a, b) => {
      const aValue = sortKey.includes('.')
        ? sortKey.split('.').reduce((obj, key) => obj?.[key], a as any)
        : a[sortKey]
      const bValue = sortKey.includes('.')
        ? sortKey.split('.').reduce((obj, key) => obj?.[key], b as any)
        : b[sortKey]

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortKey, sortDirection, sortable])

  const handleSort = (key: string) => {
    if (!sortable) return
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-neutral-100 dark:bg-neutral-800 rounded-t-lg" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800" />
        ))}
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-8 text-center">
        <p className="text-neutral-500">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={cn('overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-800', className)}>
      <table className="w-full">
        <thead>
          <tr className="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
            {columns.map((column) => (
              <th
                key={String(column.key)}
                className={cn(
                  'px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300',
                  sortable && column.sortable !== false && 'cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700',
                  column.width
                )}
                onClick={() => column.sortable !== false && handleSort(String(column.key))}
              >
                <div className="flex items-center gap-2">
                  {column.header}
                  {sortKey === column.key && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <AnimatePresence>
            {sortedData.map((item, index) => (
              <motion.tr
                key={keyExtractor(item)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: index * 0.02 }}
                className={cn(
                  'border-b border-neutral-100 dark:border-neutral-800 last:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800'
                )}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => (
                  <td key={String(column.key)} className="px-4 py-3 text-sm">
                    {column.render
                      ? column.render(column.key.toString().includes('.')
                          ? column.key.toString().split('.').reduce((obj, key) => obj?.[key], item as any)
                          : item[column.key as keyof T], item)
                      : column.key.toString().includes('.')
                        ? column.key.toString().split('.').reduce((obj, key) => obj?.[key], item as any)
                        : String(item[column.key as keyof T] ?? '')}
                  </td>
                ))}
              </motion.tr>
            ))}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  )
}

// ================================================================================
// MODAL COMPONENT
// ================================================================================
interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  description?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showClose?: boolean
  className?: string
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showClose = true,
  className,
}) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-4xl',
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={cn(
                'bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full',
                sizeClasses[size],
                className
              )}
            >
              {(title || showClose) && (
                <div className="flex items-start justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
                  <div>
                    {title && <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">{title}</h2>}
                    {description && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{description}</p>}
                  </div>
                  {showClose && (
                    <button
                      onClick={onClose}
                      className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              <div className="p-6">{children}</div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

// ================================================================================
// TABS COMPONENT
// ================================================================================
interface Tab {
  id: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
  count?: number
}

interface TabsProps {
  tabs: Tab[]
  activeTab: string
  onChange: (tabId: string) => void
  variant?: 'default' | 'pills' | 'underline'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  onChange,
  variant = 'default',
  size = 'md',
  className,
}) => {
  const sizeClasses = {
    sm: 'text-xs px-3 py-1.5',
    md: 'text-sm px-4 py-2',
    lg: 'text-base px-5 py-2.5',
  }

  const variantStyles = {
    default: {
      container: 'bg-neutral-100 dark:bg-neutral-800 rounded-lg p-1',
      tab: 'rounded-md',
      active: 'bg-white dark:bg-neutral-700 shadow-sm',
    },
    pills: {
      container: 'gap-2',
      tab: 'rounded-full',
      active: 'bg-primary-500 text-white',
    },
    underline: {
      container: 'border-b border-neutral-200 dark:border-neutral-700',
      tab: 'rounded-none border-b-2 -mb-px',
      active: 'border-primary-500 text-primary-600 dark:text-primary-400',
    },
  }

  const styles = variantStyles[variant]

  return (
    <div className={cn('flex', styles.container, className)}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => !tab.disabled && onChange(tab.id)}
          disabled={tab.disabled}
          className={cn(
            'relative font-medium transition-all duration-200',
            sizeClasses[size],
            styles.tab,
            activeTab === tab.id
              ? styles.active
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white',
            tab.disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="flex items-center gap-2">
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && (
              <span className={cn(
                'px-1.5 py-0.5 text-xs rounded-full',
                activeTab === tab.id
                  ? 'bg-white/20 dark:bg-neutral-600'
                  : 'bg-neutral-200 dark:bg-neutral-700'
              )}>
                {tab.count}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  )
}

// ================================================================================
// ACCORDION COMPONENT
// ================================================================================
interface AccordionItem {
  id: string
  title: string
  description?: string
  content: React.ReactNode
}

interface AccordionProps {
  items: AccordionItem[]
  allowMultiple?: boolean
  defaultOpen?: string[]
  className?: string
}

export const Accordion: React.FC<AccordionProps> = ({
  items,
  allowMultiple = false,
  defaultOpen = [],
  className,
}) => {
  const [openItems, setOpenItems] = useState<string[]>(defaultOpen)

  const toggleItem = (id: string) => {
    if (allowMultiple) {
      setOpenItems(prev =>
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      )
    } else {
      setOpenItems(prev => (prev.includes(id) ? [] : [id]))
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      {items.map((item) => {
        const isOpen = openItems.includes(item.id)
        return (
          <div
            key={item.id}
            className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden"
          >
            <button
              onClick={() => toggleItem(item.id)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <div>
                <h3 className="font-medium text-neutral-900 dark:text-white">{item.title}</h3>
                {item.description && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{item.description}</p>
                )}
              </div>
              <motion.span
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="text-neutral-400"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </motion.span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="p-4 pt-0 border-t border-neutral-200 dark:border-neutral-800">
                    {item.content}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

// ================================================================================
// INPUT FIELD COMPONENT
// ================================================================================
interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  containerClassName?: string
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, error, hint, leftIcon, rightIcon, containerClassName, className, ...props }, ref) => {
    return (
      <div className={containerClassName}>
        {label && (
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            className={cn(
              'w-full px-4 py-2.5 rounded-lg border bg-white dark:bg-neutral-800',
              'text-neutral-900 dark:text-white placeholder:text-neutral-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
              'transition-all duration-200',
              error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-neutral-200 dark:border-neutral-700',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="mt-1.5 text-sm text-red-500">{error}</p>}
        {hint && !error && <p className="mt-1.5 text-sm text-neutral-500">{hint}</p>}
      </div>
    )
  }
)
InputField.displayName = 'InputField'

// ================================================================================
// SELECT/DROPDOWN COMPONENT
// ================================================================================
interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  error?: string
  disabled?: boolean
  className?: string
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  error,
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption = options.find(opt => opt.value === value)

  return (
    <div className={className} ref={selectRef}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'w-full px-4 py-2.5 rounded-lg border bg-white dark:bg-neutral-800',
            'text-left flex items-center justify-between',
            'focus:outline-none focus:ring-2 focus:ring-primary-500',
            'transition-all duration-200',
            error
              ? 'border-red-500'
              : 'border-neutral-200 dark:border-neutral-700',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <span className={selectedOption ? 'text-neutral-900 dark:text-white' : 'text-neutral-400'}>
            {selectedOption?.label || placeholder}
          </span>
          <svg
            className={cn('w-5 h-5 text-neutral-400 transition-transform', isOpen && 'rotate-180')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-50 w-full mt-1 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-lg max-h-60 overflow-auto"
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => {
                    onChange(option.value)
                    setIsOpen(false)
                  }}
                  className={cn(
                    'w-full px-4 py-2.5 text-left transition-colors',
                    option.value === value
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
                    option.disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {error && <p className="mt-1.5 text-sm text-red-500">{error}</p>}
    </div>
  )
}

export default {
  AnimatedCounter,
  StatsCard,
  DataTable,
  Modal,
  Tabs,
  Accordion,
  InputField,
  Select,
}